import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import maplibregl from 'maplibre-gl';
import Sidebar from '../components/Sidebar';
import SlideOver from '../components/SlideOver';
import Tooltip from '../components/widgets/Tooltip';

function LBMap() {

	const mapContainerRef = useRef(null);
	const tooltipRef = useRef(new maplibregl.Popup({ offset: 5, closeButton: false }));

	const [featureProperties, setFeatureProperties] = useState(null);
	const [mapFilters, setMapFilters] = useState([]);
	const [multiSelectFilter, setMultiSelectFilter] = useState([]);

	const [map, setMap] = useState(null);
	const [textOptions, setTextOptions] = useState([]);
	const [familyOptions, setFamilyOptions] = useState([]);


	const [dateRange, setDateRange] = useState([1750, 1930]);
	const [startDate, setStartDate] = useState(dateRange[0]);
	const [endDate, setEndDate] = useState(dateRange[1]);

	const [mapType, setMapType] = useState('points');



	useEffect(() => {

		const map = new maplibregl.Map({
			container:  mapContainerRef.current,
			style: 'https://api.maptiler.com/maps/outdoor/style.json?key=fL9NKV06gTKowpkIEnt4', // stylesheet location
			center: [-3.232759, 54.394951], // starting position [lng, lat]
			zoom: 5 // starting zoom
		});

		map.on('load', () => {
			map.addSource('historic-os', {
				'type': 'raster',
				'url': 'https://api.maptiler.com/tiles/uk-osgb63k1885/tiles.json?key=fL9NKV06gTKowpkIEnt4',
				'tileSize': 256
			});

			map.addLayer({
				'id': 'os',
				'type': 'raster',
				'source': 'historic-os'
			});

			map.addSource('plants-source', {
				'type': 'vector',
				'url': 'https://linguabotanica.memorymapper.org/map/tiles/plants.json',
				'promoteId': 'id'
			});

			map.addSource('grid-source', {
				'type': 'vector',
				'url': 'https://linguabotanica.memorymapper.org/map/tiles/grid.json',
				'promoteId': 'id'
			});

			map.addLayer({
				id: 'plants-layer',
				source: 'plants-source',
				'source-layer': 'plants',
				type: 'circle',
				'paint': {
					'circle-color': ['get', 'color'],
					'circle-radius': [
						'interpolate',
						['linear'],
						['zoom'],
						0,
						0.5,
						22,
						10
					]
				}
			});

			map.addLayer({
				id: 'grid-layer',
				source: 'grid-source',
				'source-layer': 'grid',
				type: 'fill',
				'layout': {
					'visibility': 'none'
				},
				'paint': {
					'fill-color': [
						'interpolate',
						['linear'],
						['get', 'location_quotient'],
						0,
						'rgba(58, 76, 192, 0.8)',
						24,
						'rgba(220, 220, 221, 0.5)',
						47,
						'rgba(179, 3, 38, 0.7)',
						92,
						'rgba(179, 3, 38, 0.8)'
					]
				}
			});

			map.addLayer({
				id: 'plants-layer-heatmap',
				source: 'plants-source',
				'source-layer': 'plants',
				type: 'heatmap',
				'layout': {
					'visibility': 'none'
				},
				'paint': {
					'heatmap-radius': 15,
					'heatmap-color': [
						'interpolate',
						['linear'],
						['heatmap-density'],
						0,
						'rgba(58, 76, 192, 0)',
						0.2,
						'#007f66',
						0.4,
						'#7fbf66',
						0.6,
						'#7fbf66',
						0.8,
						'#ffff66',
						1,
						'#ffff66',
					],
					'heatmap-radius': [
						'interpolate',
						['linear'],
						['zoom'],
						0,
						2,
						18,
						20
					],
				}
			});

	        map.on('mouseenter', 'plants-layer', e => {
		      if (e.features.length) {
		        map.getCanvas().style.cursor = 'pointer';
		      }
		    });

		    // reset cursor to default when user is no longer hovering over a clickable feature
		    map.on('mouseleave', 'plants-layer', () => {
		      map.getCanvas().style.cursor = '';
		      tooltipRef.current.remove();
		    });

	        // add tooltip when users mouse move over a point
		    map.on('mousemove', e => {
		      const features = map.queryRenderedFeatures(e.point);
		      if (features.length) {
		        const feature = features[0];
		        if (feature.source == 'plants-source') {
			        // Create tooltip node
			        const tooltipNode = document.createElement('div');
			        ReactDOM.render(<Tooltip feature={feature} featureProperties={featureProperties} />, tooltipNode);

			        // Set tooltip on map
			        tooltipRef.current
			          .setLngLat(e.lngLat)
			          .setDOMContent(tooltipNode)
			          .addTo(map);
			      }
		  		}
		    });

	        setMap(map);

		});

		return () => map.remove();

	}, []);

	useEffect(() => {
		fetch('https://linguabotanica.memorymapper.org/api/1.0/texts/')
		.then(response => response.json())
		.then(json => {
			const newTextOptions = [...textOptions, ...json];
			setTextOptions(newTextOptions);
		});
	}, []);

	useEffect(() => {
		fetch('https://linguabotanica.memorymapper.org/api/1.0/taxonomy/families/')
		.then(response => response.json())
		.then(json => {
			const newFamilyOptions = [...familyOptions, ...json];
			setFamilyOptions(newFamilyOptions)
		});
	}, [])

	useEffect(() => {
		if (map) {
			const startDateFilter = ['>=', ['get', 'date_part'], startDate];
			const endDateFilter = ['<=', ['get', 'date_part'], endDate];
			//const newMapFilters = [mapFilters, ...startDateFilter, ...endDateFilter];
			//newMapFilters = [mapFilters, ...endDateFilter];
			if (multiSelectFilter.length > 0) { 
				const newMapFilters = ['all', startDateFilter, endDateFilter, multiSelectFilter];
				setMapFilters(newMapFilters);
			} else {
				const newMapFilters = ['all', startDateFilter, endDateFilter];
				setMapFilters(newMapFilters);
			}
		}
	}, [startDate, endDate]);

	useEffect(() => {
		if (map) {
			map.setFilter('plants-layer', mapFilters);
			map.setFilter('plants-layer-heatmap', mapFilters);
		}
	}, [mapFilters]);

	useEffect(() => {
		if (map) {
			map.setFilter('plants-layer', multiSelectFilter);
			map.setFilter('plants-layer-heatmap', multiSelectFilter);
		}
	}, [multiSelectFilter]);

	useEffect(() => {
		if (map) {
			if (mapType == 'points') {
				map.setLayoutProperty('plants-layer', 'visibility', 'visible');
				map.setLayoutProperty('plants-layer-heatmap', 'visibility', 'none');
				map.setLayoutProperty('grid-layer', 'visibility', 'none');
			} else if (mapType == 'heat') {
				map.setLayoutProperty('plants-layer', 'visibility', 'none');
				map.setLayoutProperty('plants-layer-heatmap', 'visibility', 'visible');
				map.setLayoutProperty('grid-layer', 'visibility', 'none');
			} else if (mapType == 'grid') {
				map.setLayoutProperty('plants-layer', 'visibility', 'none');
				map.setLayoutProperty('plants-layer-heatmap', 'visibility', 'none');
				map.setLayoutProperty('grid-layer', 'visibility', 'visible');
			}
		}
	}, [mapType])


	return (
		
		<div className='h-screen w-screen flex flex-row -mt-16 pt-16'>
			<div className="hidden md:flex md:w-1/4 md:flex-col md:inset-y-0 h-full">
				<div className="flex flex-col flex-grow pt-5 overflow-y-auto shadow text-indigo bg-white">
			        <div className="mt-5 flex-1 flex flex-col px-4 font-serif relative z-50">
						<Sidebar featureProperties={featureProperties} mapFilters={mapFilters} setMapFilters={setMapFilters} multiSelectFilter={multiSelectFilter} setMultiSelectFilter={setMultiSelectFilter} textOptions={textOptions} familyOptions={familyOptions} setStartDate={setStartDate} startDate={startDate} dateRange={dateRange} setDateRange={setDateRange} endDate={endDate} setEndDate={setEndDate} mapType={mapType} setMapType={setMapType}/>
					</div>
				</div>
			</div>
			<div className="md:flex md:w-3/4 md:flex-col md:inset-y-0 z-10">
				<div ref={mapContainerRef} className='h-full z-10'></div>
			</div>
		</div>
	)
}

export default LBMap;